import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Music as MusicIcon, Database, Trash2, Lock, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/upr-api";
import FileUpload from "@/components/FileUpload";
import { useSettings } from "@/contexts/SettingsContext";
import RichTextEditor from "@/components/RichTextEditor";

const FONT_OPTIONS = [
  { value: "sans", label: "Sans-Serif", sample: "Cabinet Grotesk + Satoshi", preview: { heading: "'Cabinet Grotesk', sans-serif", body: "'Satoshi', sans-serif" } },
  { value: "serif", label: "Serif", sample: "Playfair Display + Lora", preview: { heading: "'Playfair Display', Georgia, serif", body: "'Lora', Georgia, serif" } },
  { value: "mono", label: "Mono", sample: "JetBrains Mono", preview: { heading: "'JetBrains Mono', monospace", body: "'JetBrains Mono', monospace" } },
];

const DEFAULTS = {
  logo_url: "",
  logo_text: "UNIVERSO POSITIVO",
  logo_tagline: "Records · Est. since the sun",
  use_default_logo: true,
  primary_color: "#D95A3B",
  font_family: "sans",
  heading_scale: 1.0,
  body_size: "base",
  site_title: "Universo Positivo Records",
  meta_description: "Independent record label for soulful, sun-soaked, hand-played music. Lisbon × Amsterdam.",
  footer_tagline: "Music from the sun. Hand-pressed in Lisbon.",
  footer_about: "Universo Positivo Records is an independent label releasing soulful, sun-soaked, hand-played music.",
  footer_copyright: "© {year} Universo Positivo Records. All rights reserved.",
  footer_url: "www.universopositivorecords.com",
  hero_title_line1: "Music",
  hero_title_line2: "from the sun.",
  hero_subtitle: "Universo Positivo is an independent record label rooted in Lisbon and Amsterdam, releasing handmade music that travels between sunlit rooms and late-night dance floors.",
  hero_html: "",
  hero_font: "sans",
  marquee_words: "UNIVERSO POSITIVO, MUSIC FROM THE SUN, EST. SINCE THE SUN, LISBOA × AMSTERDAM, UPR RECORDS",
  featured_image_size: "medium",
  logo_height_px: 40,
  logo_icon_url: "",
  logo_icon_size_px: 32,
};

export default function SettingsAdmin() {
  const { settings, refresh, update } = useSettings();
  const [form, setForm] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (settings && !loaded) {
      setForm({
        logo_url: settings.logo_url || "",
        logo_text: settings.logo_text || DEFAULTS.logo_text,
        logo_tagline: settings.logo_tagline || DEFAULTS.logo_tagline,
        use_default_logo: settings.use_default_logo !== false,
        primary_color: settings.primary_color || DEFAULTS.primary_color,
        font_family: settings.font_family || "sans",
        heading_scale: settings.heading_scale ?? 1.0,
        body_size: settings.body_size || "base",
        site_title: settings.site_title || "",
        meta_description: settings.meta_description || "",
        footer_tagline: settings.footer_tagline || "",
        footer_about: settings.footer_about || "",
        footer_copyright: settings.footer_copyright ?? DEFAULTS.footer_copyright,
        footer_url: settings.footer_url ?? DEFAULTS.footer_url,
        hero_title_line1: settings.hero_title_line1 || DEFAULTS.hero_title_line1,
        hero_title_line2: settings.hero_title_line2 || DEFAULTS.hero_title_line2,
        hero_subtitle: settings.hero_subtitle || DEFAULTS.hero_subtitle,
        hero_html: settings.hero_html ?? DEFAULTS.hero_html,
        hero_font: settings.hero_font || DEFAULTS.hero_font,
        marquee_words: settings.marquee_words || DEFAULTS.marquee_words,
        featured_image_size: settings.featured_image_size || "medium",
        logo_height_px: Number(settings.logo_height_px) || 40,
        logo_icon_url: settings.logo_icon_url || "",
        logo_icon_size_px: Number(settings.logo_icon_size_px) || 32,
      });
      setLoaded(true);
    }
  }, [settings, loaded]);

  useEffect(() => {
    if (!loaded) return;
    update(form);
  }, [form, loaded, update]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", form);
      await refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm("Reset all settings to defaults? (You still need to click Save.)")) return;
    setForm(DEFAULTS);
  };

  // ---- Storage admin (free up MongoDB Atlas space) ----
  const [storage, setStorage] = useState(null);
  const [storageBusy, setStorageBusy] = useState(false);

  const loadStorage = async () => {
    setStorageBusy(true);
    try {
      const { data } = await api.get("/admin/storage-stats");
      setStorage(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load storage stats");
    } finally {
      setStorageBusy(false);
    }
  };

  useEffect(() => { loadStorage(); }, []);

  const purgeStorage = async (kind = "") => {
    const label = kind ? `all ${kind} uploads` : "ALL uploaded files (every image + audio in GridFS)";
    if (!window.confirm(`Delete ${label}? This frees Atlas space but content URLs that point to deleted files will 404. Continue?`)) return;
    setStorageBusy(true);
    try {
      const { data } = await api.delete("/admin/storage-purge", { params: { confirm: "PURGE", kind } });
      toast.success(`Deleted ${data.deleted} files · freed ${data.freed_mb} MB`);
      await loadStorage();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purge failed");
    } finally {
      setStorageBusy(false);
    }
  };

  return (
    <section data-testid="settings-admin">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-heading font-extrabold text-2xl tracking-tight">Site Settings</h2>
        <div className="flex gap-2">
          <button className="upr-btn upr-btn-ghost" onClick={resetToDefaults} type="button" data-testid="settings-reset">
            <RotateCcw size={12} /> Reset
          </button>
          <button className="upr-btn" onClick={save} type="button" disabled={busy} data-testid="settings-save">
            <Save size={14} /> {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>

      <p className="text-sm mb-8" style={{ color: "var(--upr-muted)" }}>
        Changes preview live below; click <strong>Save settings</strong> to persist them for every visitor.
      </p>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Logo & Branding */}
        <div className="space-y-6">
          <ChangePasswordPanel />

          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1">Logo &amp; Branding</h3>
            <p className="text-sm mb-4" style={{ color: "var(--upr-muted)" }}>
              Customize the header / footer logo. Toggle between the default spinning vinyl + text, or your own image.
            </p>

            <label className="flex items-center gap-2 mb-3 cursor-pointer" data-testid="settings-use-default-logo-label">
              <input
                type="checkbox"
                checked={form.use_default_logo !== false}
                onChange={(e) => setField("use_default_logo", e.target.checked)}
                data-testid="settings-use-default-logo"
              />
              <span className="text-sm">Use the default spinning vinyl + text logo</span>
            </label>

            {form.use_default_logo !== false ? (
              <>
                <label className="block mb-3">
                  <span className="upr-eyebrow block mb-2">Logo text (header + footer)</span>
                  <input
                    type="text"
                    value={form.logo_text}
                    onChange={(e) => setField("logo_text", e.target.value)}
                    maxLength={80}
                    data-testid="settings-logo-text"
                    className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                    style={{ borderColor: "var(--upr-border)" }}
                  />
                </label>
                <label className="block mb-3">
                  <span className="upr-eyebrow block mb-2">Logo tagline (small text under header logo)</span>
                  <input
                    type="text"
                    value={form.logo_tagline}
                    onChange={(e) => setField("logo_tagline", e.target.value)}
                    maxLength={120}
                    data-testid="settings-logo-tagline"
                    className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                    style={{ borderColor: "var(--upr-border)" }}
                  />
                </label>

                {/* Custom round icon image (replaces spinning vinyl, keeps wordmark) */}
                <div className="mt-4 p-3" style={{ border: "1px dashed var(--upr-border)", background: "var(--upr-surface)" }}>
                  <div className="upr-eyebrow mb-2">Round icon image (optional)</div>
                  <p className="text-xs mb-3" style={{ color: "var(--upr-muted)" }}>
                    Upload your <strong>round label logo</strong> to replace the spinning vinyl record next to the wordmark. Leave empty to keep the default spinning record.
                  </p>
                  <FileUpload
                    label="Round icon image (PNG / SVG, square or round, transparent bg ideal)"
                    value={form.logo_icon_url}
                    onChange={(v) => setField("logo_icon_url", v)}
                    kind="image"
                    testid="settings-logo-icon"
                  />
                  {form.logo_icon_url && (
                    <button
                      type="button"
                      onClick={() => setField("logo_icon_url", "")}
                      className="upr-btn upr-btn-ghost mt-2"
                      data-testid="settings-logo-icon-clear"
                    >
                      Remove icon (restore spinning record)
                    </button>
                  )}
                  <div className="mt-3">
                    <span className="upr-eyebrow block mb-2">Icon size</span>
                    <div className="flex items-center gap-3" data-testid="settings-logo-icon-size-wrap">
                      <input
                        type="range"
                        min="24"
                        max="96"
                        step="2"
                        value={form.logo_icon_size_px}
                        onChange={(e) => setField("logo_icon_size_px", parseInt(e.target.value, 10))}
                        data-testid="settings-logo-icon-size"
                        className="flex-1"
                      />
                      <input
                        type="number"
                        min="24"
                        max="96"
                        step="2"
                        value={form.logo_icon_size_px}
                        onChange={(e) => {
                          const v = Math.max(24, Math.min(96, parseInt(e.target.value || "32", 10)));
                          setField("logo_icon_size_px", v);
                        }}
                        data-testid="settings-logo-icon-size-input"
                        className="w-20 p-2 bg-transparent border font-mono text-sm"
                        style={{ borderColor: "var(--upr-border)" }}
                      />
                      <span className="text-xs" style={{ color: "var(--upr-muted)" }}>px</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
                      Default spinning record is 32 px. Try 40–56 px for a slightly bigger icon.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <FileUpload
                  label="Custom logo image (PNG / SVG — transparent background recommended)"
                  value={form.logo_url}
                  onChange={(v) => setField("logo_url", v)}
                  kind="image"
                  testid="settings-logo"
                />
                <div className="mt-4">
                  <span className="upr-eyebrow block mb-2">Header logo size</span>
                  <div className="flex items-center gap-3" data-testid="settings-logo-height-wrap">
                    <input
                      type="range"
                      min="24"
                      max="160"
                      step="2"
                      value={form.logo_height_px}
                      onChange={(e) => setField("logo_height_px", parseInt(e.target.value, 10))}
                      data-testid="settings-logo-height"
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min="24"
                      max="160"
                      step="2"
                      value={form.logo_height_px}
                      onChange={(e) => {
                        const v = Math.max(24, Math.min(160, parseInt(e.target.value || "40", 10)));
                        setField("logo_height_px", v);
                      }}
                      data-testid="settings-logo-height-input"
                      className="w-20 p-2 bg-transparent border font-mono text-sm"
                      style={{ borderColor: "var(--upr-border)" }}
                    />
                    <span className="text-xs" style={{ color: "var(--upr-muted)" }}>px</span>
                  </div>
                  <div className="mt-3 p-3 flex items-center" style={{ border: "1px solid var(--upr-border)", background: "var(--upr-surface)", minHeight: 80 }}>
                    {form.logo_url ? (
                      <img
                        src={form.logo_url}
                        alt="Logo preview"
                        style={{ height: `${form.logo_height_px}px`, maxWidth: "320px", objectFit: "contain" }}
                        data-testid="settings-logo-preview"
                      />
                    ) : (
                      <span className="text-xs" style={{ color: "var(--upr-muted)" }}>Upload an image above to preview at this size.</span>
                    )}
                  </div>
                  <p className="text-xs mt-2" style={{ color: "var(--upr-muted)" }}>
                    Default spinning vinyl is ~32 px. Try 48–64 px for a bigger logo. Footer uses ~1.2× this height.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Visual customization */}
          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1">Visual Customization</h3>
            <p className="text-sm mb-4" style={{ color: "var(--upr-muted)" }}>Brand color drives buttons, links and accents site-wide.</p>

            <label className="block mb-4">
              <span className="upr-eyebrow block mb-2">Primary brand color</span>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setField("primary_color", e.target.value.toUpperCase())}
                  data-testid="settings-color-picker"
                  className="w-14 h-12 cursor-pointer border"
                  style={{ borderColor: "var(--upr-border)", background: "transparent" }}
                />
                <input
                  type="text"
                  value={form.primary_color}
                  onChange={(e) => setField("primary_color", e.target.value.toUpperCase())}
                  data-testid="settings-color-hex"
                  className="flex-1 p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] font-mono"
                  style={{ borderColor: "var(--upr-border)" }}
                  placeholder="#D95A3B"
                />
                <span className="upr-btn" style={{ pointerEvents: "none" }}>Preview</span>
              </div>
            </label>

            <div>
              <span className="upr-eyebrow block mb-2">Global font</span>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="settings-font-options">
                {FONT_OPTIONS.map((opt) => {
                  const active = form.font_family === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setField("font_family", opt.value)}
                      data-testid={`settings-font-${opt.value}`}
                      className="text-left p-3 border transition-colors"
                      style={{
                        borderColor: active ? "var(--upr-primary)" : "var(--upr-border)",
                        background: active ? "var(--upr-surface)" : "transparent",
                      }}
                    >
                      <div className="upr-eyebrow" style={{ color: active ? "var(--upr-primary)" : undefined }}>
                        {opt.label}
                      </div>
                      <div className="mt-2 text-xl" style={{ fontFamily: opt.preview.heading }}>Aa</div>
                      <div className="mt-1 text-xs" style={{ fontFamily: opt.preview.body, color: "var(--upr-muted)" }}>
                        {opt.sample}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <span className="upr-eyebrow block mb-2">Heading scale (H1 / H2 / H3)</span>
              <div className="flex items-center gap-3" data-testid="settings-heading-scale-wrap">
                <input
                  type="range"
                  min="0.7"
                  max="1.6"
                  step="0.05"
                  value={form.heading_scale}
                  onChange={(e) => setField("heading_scale", parseFloat(e.target.value))}
                  data-testid="settings-heading-scale"
                  className="flex-1"
                />
                <span className="font-mono text-sm" style={{ minWidth: 50 }}>{Number(form.heading_scale).toFixed(2)}×</span>
              </div>
              <div className="mt-3 p-3" style={{ border: "1px solid var(--upr-border)" }}>
                <div className="upr-h1 font-heading font-extrabold" style={{ lineHeight: 1 }}>H1 Heading</div>
                <div className="upr-h2 font-heading font-extrabold mt-1">H2 Subheading</div>
                <div className="upr-h3 font-heading font-extrabold mt-1">H3 Section title</div>
              </div>
            </div>

            <div className="mt-4">
              <span className="upr-eyebrow block mb-2">Body text size</span>
              <div className="flex gap-2" data-testid="settings-body-size-wrap">
                {[
                  { v: "sm", label: "Small (14px)" },
                  { v: "base", label: "Base (16px)" },
                  { v: "lg", label: "Large (18px)" },
                ].map((opt) => {
                  const active = form.body_size === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setField("body_size", opt.v)}
                      data-testid={`settings-body-${opt.v}`}
                      className="font-heading uppercase tracking-widest text-xs px-3 py-1.5 border transition-colors"
                      style={{
                        background: active ? "var(--upr-fg)" : "transparent",
                        color: active ? "var(--upr-bg)" : "var(--upr-fg)",
                        borderColor: active ? "var(--upr-fg)" : "var(--upr-border)",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* SEO + Footer + Music */}
        <div className="space-y-6">
          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1">Music Page Hero</h3>
            <p className="text-sm mb-4" style={{ color: "var(--upr-muted)" }}>
              The big headline, intro paragraph and scrolling banner shown at the top of the <strong>Music</strong> page. (To choose which release is featured, edit it under <em>Releases</em> and tick &quot;Featured on Music page&quot;.)
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <label className="block">
                <span className="upr-eyebrow block mb-2">Hero title — line 1</span>
                <input
                  type="text"
                  value={form.hero_title_line1}
                  onChange={(e) => setField("hero_title_line1", e.target.value)}
                  maxLength={80}
                  data-testid="settings-hero-title-line1"
                  className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                  style={{ borderColor: "var(--upr-border)" }}
                />
              </label>
              <label className="block">
                <span className="upr-eyebrow block mb-2">Hero title — line 2 (colored)</span>
                <input
                  type="text"
                  value={form.hero_title_line2}
                  onChange={(e) => setField("hero_title_line2", e.target.value)}
                  maxLength={80}
                  data-testid="settings-hero-title-line2"
                  className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                  style={{ borderColor: "var(--upr-border)" }}
                />
              </label>
            </div>

            <div className="mb-3 p-4 border" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="upr-eyebrow">Rich-text hero (overrides the two title lines when filled)</span>
                <label className="flex items-center gap-2 text-xs">
                  <span className="upr-eyebrow" style={{ fontSize: "0.62rem" }}>Font</span>
                  <select
                    value={form.hero_font || "sans"}
                    onChange={(e) => setField("hero_font", e.target.value)}
                    data-testid="settings-hero-font"
                    className="p-1.5 bg-transparent border font-heading uppercase tracking-widest text-[0.7rem]"
                    style={{ borderColor: "var(--upr-border)" }}
                  >
                    <option value="sans">Sans (Cabinet Grotesk)</option>
                    <option value="serif">Serif (Playfair Display)</option>
                    <option value="display">Display (Jost)</option>
                  </select>
                </label>
              </div>
              <RichTextEditor
                value={form.hero_html || ""}
                onChange={(v) => setField("hero_html", v)}
                testid="settings-hero-html"
                simple
                placeholder="Leave blank to keep the two title lines above. Fill in for a custom rich-text hero — first heading = line 1, second heading or paragraph = line 2 / subtitle. Bold and italic supported."
              />
              <p className="mt-2 text-xs" style={{ color: "var(--upr-muted)" }}>
                Tip: use <strong>H1</strong> for the giant title, <strong>H2</strong> for a smaller secondary line, and <strong>a paragraph</strong> for the intro copy — all rendered at the hero&apos;s scale.
              </p>
            </div>

            <label className="block mb-3">
              <span className="upr-eyebrow block mb-2">Hero subtitle (fallback — used when rich-text hero is blank)</span>
              <textarea
                value={form.hero_subtitle}
                onChange={(e) => setField("hero_subtitle", e.target.value)}
                rows={4}
                maxLength={600}
                data-testid="settings-hero-subtitle"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              />
              <span className="block text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
                {(form.hero_subtitle || "").length}/600
              </span>
            </label>
            <label className="block mb-3">
              <span className="upr-eyebrow block mb-2">Scrolling banner words (comma-separated)</span>
              <input
                type="text"
                value={form.marquee_words}
                onChange={(e) => setField("marquee_words", e.target.value)}
                maxLength={600}
                data-testid="settings-marquee-words"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
                placeholder="UNIVERSO POSITIVO, MUSIC FROM THE SUN, ..."
              />
              <span className="block text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
                Each comma-separated value becomes one item in the marquee. Leave empty to hide the banner.
              </span>
            </label>
            <div>
              <span className="upr-eyebrow block mb-2">Featured release image size</span>
              <div className="flex gap-2" data-testid="settings-featured-size-wrap">
                {[
                  { v: "small", label: "Small" },
                  { v: "medium", label: "Medium" },
                  { v: "large", label: "Large" },
                ].map((opt) => {
                  const active = form.featured_image_size === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setField("featured_image_size", opt.v)}
                      data-testid={`settings-featured-size-${opt.v}`}
                      className="font-heading uppercase tracking-widest text-xs px-3 py-1.5 border transition-colors"
                      style={{
                        background: active ? "var(--upr-fg)" : "transparent",
                        color: active ? "var(--upr-bg)" : "var(--upr-fg)",
                        borderColor: active ? "var(--upr-fg)" : "var(--upr-border)",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1">Footer Text</h3>
            <p className="text-sm mb-4" style={{ color: "var(--upr-muted)" }}>
              Custom copy shown in the site footer beneath the logo.
            </p>
            <label className="block mb-3">
              <span className="upr-eyebrow block mb-2">Footer tagline (one short line)</span>
              <input
                type="text"
                value={form.footer_tagline}
                onChange={(e) => setField("footer_tagline", e.target.value)}
                maxLength={200}
                data-testid="settings-footer-tagline"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              />
            </label>
            <label className="block">
              <span className="upr-eyebrow block mb-2">Footer about (1–3 sentences)</span>
              <textarea
                value={form.footer_about}
                onChange={(e) => setField("footer_about", e.target.value)}
                rows={3}
                maxLength={400}
                data-testid="settings-footer-about"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              />
            </label>
            <label className="block mt-3">
              <span className="upr-eyebrow block mb-2">Footer copyright line ({'{year}'} is replaced with the current year)</span>
              <input
                type="text"
                value={form.footer_copyright}
                onChange={(e) => setField("footer_copyright", e.target.value)}
                maxLength={240}
                data-testid="settings-footer-copyright"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              />
            </label>
            <label className="block mt-3">
              <span className="upr-eyebrow block mb-2">Footer URL (right-aligned line)</span>
              <input
                type="text"
                value={form.footer_url}
                onChange={(e) => setField("footer_url", e.target.value)}
                maxLength={240}
                data-testid="settings-footer-url"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              />
            </label>
          </div>
          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1">SEO Settings</h3>
            <p className="text-sm mb-4" style={{ color: "var(--upr-muted)" }}>
              How search engines &amp; social cards display the site.
            </p>

            <label className="block mb-4">
              <span className="upr-eyebrow block mb-2">Site title (browser tab + Google)</span>
              <input
                type="text"
                value={form.site_title}
                onChange={(e) => setField("site_title", e.target.value)}
                maxLength={120}
                data-testid="settings-site-title"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              />
              <span className="block text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
                {(form.site_title || "").length}/120
              </span>
            </label>

            <label className="block mb-4">
              <span className="upr-eyebrow block mb-2">Meta description (search snippet)</span>
              <textarea
                value={form.meta_description}
                onChange={(e) => setField("meta_description", e.target.value)}
                rows={4}
                maxLength={320}
                data-testid="settings-meta-description"
                className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
                style={{ borderColor: "var(--upr-border)" }}
              />
              <span className="block text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
                {(form.meta_description || "").length}/320 · Google shows ~155–160 chars
              </span>
            </label>

            {/* SERP-style preview */}
            <div className="p-4 border" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }} data-testid="settings-serp-preview">
              <div className="text-xs" style={{ color: "#1a0dab" }}>universopositivorecords.com</div>
              <div className="text-lg font-medium" style={{ color: "#1a0dab" }}>{form.site_title || "—"}</div>
              <div className="text-sm" style={{ color: "var(--upr-muted)" }}>
                {form.meta_description || "—"}
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1 flex items-center gap-2">
              <Sparkles size={18} /> AI &amp; Search Discovery
            </h3>
            <p className="text-sm mb-4" style={{ color: "var(--upr-muted)" }}>
              Two live endpoints are served straight from MongoDB — no static files, no button to click, no redeploy. Every request rebuilds the response from your current catalogue. <code>robots.txt</code> at the domain root points crawlers here.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-4 border" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }} data-testid="ai-discovery-llm">
                <div className="upr-eyebrow mb-2">AI crawlers</div>
                <div className="font-mono text-xs mb-3 break-all" style={{ color: "var(--upr-fg)" }}>/api/llm.txt</div>
                <p className="text-xs mb-3" style={{ color: "var(--upr-muted)" }}>
                  Plain-text summary of the label, artists and latest releases for ChatGPT / Claude / Gemini crawlers. Live from DB.
                </p>
                <a href="/api/llm.txt" target="_blank" rel="noreferrer" className="upr-btn" data-testid="ai-discovery-llm-open">
                  Open live llm.txt ↗
                </a>
              </div>
              <div className="p-4 border" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }} data-testid="ai-discovery-sitemap">
                <div className="upr-eyebrow mb-2">Search engines</div>
                <div className="font-mono text-xs mb-3 break-all" style={{ color: "var(--upr-fg)" }}>/api/sitemap.xml</div>
                <p className="text-xs mb-3" style={{ color: "var(--upr-muted)" }}>
                  Google/Bing sitemap covering every release, artist and blog post URL. Live from DB. Submit once to Search Console.
                </p>
                <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="upr-btn" data-testid="ai-discovery-sitemap-open">
                  Open live sitemap.xml ↗
                </a>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1 flex items-center gap-2">
              <Database size={18} /> Storage (MongoDB GridFS)
            </h3>
            <p className="text-sm mb-3" style={{ color: "var(--upr-muted)" }}>
              All images &amp; audio you upload are stored inside MongoDB. If you hit the Atlas storage quota (free tier = 512 MB), use this to free space by deleting old test uploads. Deletes work even when writes are blocked.
            </p>
            {storage ? (
              <div className="p-3 mb-3" style={{ border: "1px solid var(--upr-border)", background: "var(--upr-surface)" }} data-testid="storage-stats">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-heading font-extrabold text-2xl" data-testid="storage-total-mb">{storage.total_mb} MB</span>
                  <span className="text-sm" style={{ color: "var(--upr-muted)" }}>across {storage.file_count} file{storage.file_count === 1 ? "" : "s"}</span>
                </div>
                {storage.by_content_type?.length > 0 && (
                  <ul className="text-xs space-y-1" style={{ color: "var(--upr-muted)" }}>
                    {storage.by_content_type.slice(0, 6).map((t) => (
                      <li key={t.content_type} className="flex justify-between gap-3">
                        <span className="font-mono">{t.content_type}</span>
                        <span>{t.count} · {t.mb} MB</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="text-xs mb-3" style={{ color: "var(--upr-muted)" }}>{storageBusy ? "Loading stats…" : "—"}</div>
            )}
            <div className="flex flex-wrap gap-2">
              <button type="button" className="upr-btn upr-btn-ghost" onClick={loadStorage} disabled={storageBusy} data-testid="storage-refresh">
                Refresh stats
              </button>
              <button type="button" className="upr-btn upr-btn-ghost" onClick={() => purgeStorage("audio")} disabled={storageBusy || !storage?.file_count} data-testid="storage-purge-audio">
                <Trash2 size={12} /> Delete all audio
              </button>
              <button type="button" className="upr-btn upr-btn-ghost" onClick={() => purgeStorage("image")} disabled={storageBusy || !storage?.file_count} data-testid="storage-purge-image">
                <Trash2 size={12} /> Delete all images
              </button>
              <button type="button" className="upr-btn" style={{ background: "var(--upr-primary)", color: "var(--upr-bg)" }} onClick={() => purgeStorage("")} disabled={storageBusy || !storage?.file_count} data-testid="storage-purge-all">
                <Trash2 size={14} /> Delete EVERYTHING
              </button>
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--upr-muted)" }}>
              💡 Deleting files frees Atlas space, but any release / artist / merch that referenced a deleted file will show a broken image until you re-upload.
            </p>
          </div>

          <div>
            <h3 className="font-heading font-extrabold text-lg mb-1 flex items-center gap-2">
              <MusicIcon size={18} /> Music Management
            </h3>
            <p className="text-sm" style={{ color: "var(--upr-muted)" }}>
              Track uploads support both a <strong>preview MP3</strong> (public streaming player), a <strong>customer download MP3</strong> (gated, delivered post-purchase), and an optional <strong>WAV master</strong> (archive only — never sent to customers). Edit any release in the <em>Releases</em> tab.
            </p>
            <ul className="mt-3 text-sm list-disc pl-5" style={{ color: "var(--upr-muted)" }}>
              <li>Accepted preview & download: <code>audio/mpeg</code>, <code>audio/mp3</code> (128–320 kbps recommended)</li>
              <li>Accepted WAV master: <code>audio/wav</code>, <code>audio/flac</code>, <code>audio/aiff</code>, <code>audio/m4a</code> (up to 100 MB each)</li>
              <li>Bulk drop: use the <em>Import</em> tab → &quot;Bulk asset upload&quot; to drop MP3/WAV files matched by catalog number.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}


function ChangePasswordPanel() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Use the new password next time you log in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h3 className="font-heading font-extrabold text-lg mb-1 flex items-center gap-2">
        <Lock size={18} /> Admin password
      </h3>
      <p className="text-sm mb-4" style={{ color: "var(--upr-muted)" }}>
        Change the password you use to log in to <strong>/admin</strong>. Minimum 8 characters. After saving, your existing login stays active — you&apos;ll only need the new password next time you sign in.
      </p>
      <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
        <label className="block">
          <span className="upr-eyebrow block mb-2">Current password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="change-password-current"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <label className="block">
          <span className="upr-eyebrow block mb-2">New password</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            data-testid="change-password-new"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <label className="block">
          <span className="upr-eyebrow block mb-2">Confirm new password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            data-testid="change-password-confirm"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            className="upr-btn"
            disabled={busy || !current || !next || !confirm}
            data-testid="change-password-submit"
          >
            <Lock size={14} /> {busy ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
